import React from 'react';
import PropTypes from 'prop-types';
import {
  Paper, Box, makeStyles, Typography, Divider,
} from '@material-ui/core';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { MessageOverride } from 'Overrides/Message';
import MeetingInformation from 'Components/UI/MeetingInformation/MeetingInformationTooltip';
import ScrollIntoView from 'Components/UI/ScrollIntoView';
import LoadingWrapper from 'Components/UI/LoadingOverlay/LoadingWrapper';
import { useLocation } from 'react-router';
import classNames from 'classnames';
import Message from './Message';
import PostMessageField from './PostMessageField';

const useStyles = makeStyles(theme => ({
  root: {},
  messagesBox: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing(1),
    overflow: 'auto',
  },
  messagesBoxWithPosition: {
    position: 'absolute',
    bottom: 0,
  },
  messagesScrollBox: {
    overflow: 'auto',
    flexGrow: 1,
  },
  messages: {
    marginBottom: theme.spacing(2),
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column-reverse',
  },
  noMessages: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
  },
  arrow: {
    fontSize: 64,
  },
}));

export default function Messages({
  householdResource,
  meeting,
  className,
  showMeetingInformation,
  sendMessageDisabled,
  scrollMessages,
}) {
  const location = useLocation();
  const classes = useStyles();
  const [messages, setMessages] = React.useState();
  const [newMessage, setNewMessage] = React.useState();
  const [loading, setLoading] = React.useState(true);
  const messagesRef = React.useRef(null);

  const refreshMessages = () => {
    setLoading(true);
    return messages.refresh({
      metaOverrideSet: MessageOverride,
    }).then(msgs => setMessages(msgs)).catch((err) => {
      displayErrorMessage(err, err.errors);
    }).finally(() => setLoading(false));
  };

  const setNewMessageWithOverride = () => setNewMessage(messages.create({
    metaOverrideSet: MessageOverride,
  }));

  const scrollTo = location.hash === '#chat';

  React.useEffect(() => {
    setMessages(null);
    if (meeting?.relationship('messages')?.link('related')) {
      meeting
        .relationship('messages')
        .link('related')
        .Get({
          metaOverrideSet: MessageOverride,
        })
        .then((col) => {
          setMessages(col);
        })
        .catch((err) => {
          displayErrorMessage(err, err.errors);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [meeting?.id()]);

  React.useEffect(() => {
    if (messages) {
      setNewMessageWithOverride();
    }
  }, [messages, messagesRef.current]);

  const handlePostMessage = (e, value) => {
    e.preventDefault();
    setLoading(true);
    newMessage.attr('content', value);
    newMessage
      .submit()
      .then(() => {
        setNewMessageWithOverride();
        return refreshMessages();
      })
      .catch((err) => {
        displayErrorMessage(err, err.errors);
      }).finally(() => setLoading(false));
  };

  const content = (
    <Box className={classNames(classes.messagesBox, { [classes.messagesBoxWithPosition]: scrollMessages })}>
      {(showMeetingInformation && householdResource) ? (
        <MeetingInformation
          householdResource={householdResource}
          meetingResource={meeting}
        />
      ) : null}
      {scrollMessages && <div style={{ flexGrow: 1 }} />}
      {messages && (
        <div
          ref={messagesRef}
          className={classNames({
            [classes.messages]: messages.resources().length() !== 0,
            [classes.messagesScrollBox]: scrollMessages,
          })}
          test="message-container"
        >
          {messages
            .resources()
            .list()
            .reverse()
            .map(m => (
              <span key={`${m.id()}-msg-${meeting.id()}`}>
                <Message
                  resource={m}
                  setLoading={setLoading}
                  refresh={refreshMessages}
                  editDisabled={sendMessageDisabled}
                />
                <Divider />
              </span>
            ))}
        </div>
      )}
      {scrollTo && <ScrollIntoView id="chat" />}
      {newMessage && (
        <PostMessageField
          message={newMessage}
          onSave={handlePostMessage}
          sendDisabled={sendMessageDisabled}
        />
      )}
      {messages && messages.resources().length() === 0 && (
        <Paper className={classes.noMessages}>
          <KeyboardArrowUpIcon className={classes.arrow} color="secondary" />
          <Typography color="secondary" variant="h3">
                Start a conversation
          </Typography>
        </Paper>
      )}
    </Box>
  );

  if (scrollMessages && !loading) {
    return content;
  }

  return (
    <LoadingWrapper loading={loading} className={classNames(classes.root, className)}>
      {content}
    </LoadingWrapper>
  );
}

Messages.defaultProps = {
  className: '',
  householdResource: null,
  meeting: null,
  showMeetingInformation: false,
  sendMessageDisabled: false,
  scrollMessages: false,
};

Messages.propTypes = {
  meeting: PropTypes.object,
  householdResource: PropTypes.object,
  className: PropTypes.string,
  showMeetingInformation: PropTypes.bool,
  sendMessageDisabled: PropTypes.bool,
  scrollMessages: PropTypes.bool,
};
